import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { DimensionsDraft, optionsIncludingStoredValue } from './panelEdit';

const controlClasses =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

export interface PanelInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
  isCompact?: boolean;
}

export function PanelInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  maxLength,
  isCompact = false,
}: PanelInputProps) {
  return (
    <input
      type='text'
      aria-label={ariaLabel}
      className={`${controlClasses} ${isCompact ? 'w-20 text-center' : ''}`}
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.target.value)
      }
    />
  );
}

export interface PanelSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  ariaLabel: string;
  isAuto?: boolean;
  formatOption?: (option: string) => string;
}

export function PanelSelect({
  value,
  options,
  onChange,
  ariaLabel,
  isAuto = false,
  formatOption = (option) => option,
}: PanelSelectProps) {
  return (
    <select
      aria-label={ariaLabel}
      className={`${controlClasses} ${isAuto ? 'w-auto' : ''}`}
      value={value}
      onChange={(event: ChangeEvent<HTMLSelectElement>) =>
        onChange(event.target.value)
      }
    >
      {optionsIncludingStoredValue(value, options).map((option) => (
        <option key={option} value={option}>
          {formatOption(option)}
        </option>
      ))}
    </select>
  );
}

export interface PanelTextareaProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  rows?: number;
}

export function PanelTextarea({
  value,
  onChange,
  ariaLabel,
  rows = 3,
}: PanelTextareaProps) {
  return (
    <textarea
      aria-label={ariaLabel}
      className={controlClasses}
      rows={rows}
      value={value}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
        onChange(event.target.value)
      }
    />
  );
}

export function PanelControlRow({ children }: { children: ReactNode }) {
  return <div className='flex items-center gap-2'>{children}</div>;
}

export interface DimensionsEditorProps {
  label: string;
  value: DimensionsDraft;
  unitOptions: string[];
  onChange: (value: DimensionsDraft) => void;
}

export function DimensionsEditor({
  label,
  value,
  unitOptions,
  onChange,
}: DimensionsEditorProps) {
  return (
    <PanelControlRow>
      <PanelInput
        isCompact
        ariaLabel={`${label} length`}
        value={value.length}
        onChange={(length) => onChange({ ...value, length })}
      />
      <span className='text-xs text-gray-500'>×</span>
      <PanelInput
        isCompact
        ariaLabel={`${label} width`}
        value={value.width}
        onChange={(width) => onChange({ ...value, width })}
      />
      <span className='text-xs text-gray-500'>×</span>
      <PanelInput
        isCompact
        ariaLabel={`${label} height`}
        value={value.height}
        onChange={(height) => onChange({ ...value, height })}
      />
      <PanelSelect
        isAuto
        ariaLabel={`${label} unit`}
        value={value.unitOfMeasure}
        options={unitOptions}
        onChange={(unitOfMeasure) => onChange({ ...value, unitOfMeasure })}
      />
    </PanelControlRow>
  );
}

export const formatDimensions = (
  dimensions: DimensionsDraft
): string | null => {
  if (!dimensions.length || !dimensions.width || !dimensions.height)
    return null;
  return `${dimensions.length} × ${dimensions.width} × ${dimensions.height} ${dimensions.unitOfMeasure}`;
};

export interface PanelImagePickerProps {
  imageSrc: string | null;
  chosenImageFile: File | null;
  imageNoun: 'product' | 'SKU';
  altText: string;
  isChangeable: boolean;
  onImageChosen: (chosenImageFile: File) => void;
}

export function PanelImagePicker({
  imageSrc,
  chosenImageFile,
  imageNoun,
  altText,
  isChangeable,
  onImageChosen,
}: PanelImagePickerProps) {
  const [chosenImagePreview, setChosenImagePreview] = useState<string | null>(
    null
  );
  const imageFileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!chosenImageFile) {
      setChosenImagePreview(null);
      return undefined;
    }
    const preview = URL.createObjectURL(chosenImageFile);
    setChosenImagePreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [chosenImageFile]);

  const shownImageSrc = chosenImagePreview ?? imageSrc;

  const handleChooseImage = (event: ChangeEvent<HTMLInputElement>) => {
    const newImageFile = event.target.files?.[0];
    if (newImageFile) onImageChosen(newImageFile);
    if (imageFileInput.current) imageFileInput.current.value = '';
  };

  const imageTile = shownImageSrc ? (
    <img
      src={shownImageSrc}
      alt={altText}
      className='h-[140px] w-[140px] rounded-lg border border-gray-200 bg-white object-cover'
    />
  ) : (
    <div className='flex h-[140px] w-[140px] flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-white text-xs text-gray-400'>
      <PhotoIcon className='h-6 w-6 text-gray-300' />
      {isChangeable ? 'Add image' : 'No image'}
    </div>
  );

  if (!isChangeable) return <div className='shrink-0'>{imageTile}</div>;

  return (
    <div className='shrink-0'>
      <button
        type='button'
        aria-label={
          shownImageSrc ? `Change ${imageNoun} image` : `Add ${imageNoun} image`
        }
        className='group relative block rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary'
        onClick={() => imageFileInput.current?.click()}
      >
        {imageTile}
        {shownImageSrc && (
          <span className='absolute inset-x-0 bottom-0 rounded-b-lg bg-gray-900/60 py-1.5 text-center text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'>
            Change image
          </span>
        )}
      </button>
      <input
        ref={imageFileInput}
        type='file'
        accept='image/*'
        className='hidden'
        aria-hidden
        tabIndex={-1}
        onChange={handleChooseImage}
      />
    </div>
  );
}
